import { Edge, Graph, Node, Point } from '@antv/x6';
import { pointFromParentAttach } from './mergeSameTypeEdges';

const INVOCATION_ZIGZAG_HALF = 20;
const INVOCATION_ZIGZAG_OFFSET = 6;
const INVOCATION_GUIDE_TAG = '_invocationAngleGuide';

let connectorRegistered = false;
let anchorRegistered = false;

// 计算从 node 中心指向 target 点方向上，node 边界的交点
// 对 opm-process（椭圆）节点使用椭圆边界，避免矩形包围盒造成的空隙
function computeBoundaryPoint(node: Node, target: { x: number; y: number }): { x: number; y: number } {
  const bbox = node.getBBox();
  const cx = bbox.x + bbox.width / 2;
  const cy = bbox.y + bbox.height / 2;
  const dx = target.x - cx;
  const dy = target.y - cy;
  if (dx === 0 && dy === 0) return { x: cx, y: cy };

  const hw = bbox.width / 2;
  const hh = bbox.height / 2;

  // 过程节点为椭圆形，计算椭圆边界交点
  if (node.shape === 'opm-process' && hw > 0 && hh > 0) {
    const angle = Math.atan2(dy, dx);
    return {
      x: cx + hw * Math.cos(angle),
      y: cy + hh * Math.sin(angle),
    };
  }

  // 矩形/其他形状：使用包围盒交点
  const scaleX = dx !== 0 ? hw / Math.abs(dx) : Infinity;
  const scaleY = dy !== 0 ? hh / Math.abs(dy) : Infinity;
  const scale = Math.min(scaleX, scaleY);

  return { x: cx + dx * scale, y: cy + dy * scale };
}

function getNodeCenter(node: Node): { x: number; y: number } {
  const bbox = node.getBBox();
  return { x: bbox.x + bbox.width / 2, y: bbox.y + bbox.height / 2 };
}

function normalizeAngle(angle: number): number {
  let a = angle % (2 * Math.PI);
  if (a < 0) a += 2 * Math.PI;
  return a;
}

function computeInvocationFirstSegmentPoint(
  start: { x: number; y: number },
  end: { x: number; y: number },
): { x: number; y: number } {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const length = Math.hypot(dx, dy);
  if (!length) return end;
  const ux = dx / length;
  const uy = dy / length;
  const nx = -uy;
  const ny = ux;
  const midX = (start.x + end.x) / 2;
  const midY = (start.y + end.y) / 2;
  return {
    x: midX + ux * INVOCATION_ZIGZAG_HALF - nx * INVOCATION_ZIGZAG_OFFSET,
    y: midY + uy * INVOCATION_ZIGZAG_HALF - ny * INVOCATION_ZIGZAG_OFFSET,
  };
}

function findMinimalCoveringOrder(angles: number[]): number[] {
  if (angles.length <= 1) return angles.map(normalizeAngle);
  const sorted = angles.map(normalizeAngle).sort((a, b) => a - b);

  let maxGap = -1;
  let maxGapIndex = -1;
  for (let i = 0; i < sorted.length; i++) {
    const current = sorted[i];
    const next = i === sorted.length - 1 ? sorted[0] + 2 * Math.PI : sorted[i + 1];
    const gap = next - current;
    if (gap > maxGap) {
      maxGap = gap;
      maxGapIndex = i;
    }
  }

  const intervalStart = sorted[(maxGapIndex + 1) % sorted.length];
  return sorted
    .map(a => (a < intervalStart ? a + 2 * Math.PI : a))
    .sort((a, b) => a - b);
}

const isInvocationAngleGuide = (data: any) => !!data?.[INVOCATION_GUIDE_TAG];

// 强制重新渲染指定边
export function forceEdgeRerender(edge: Edge, graph: Graph): void {
  const edgeView = graph.findViewByCell(edge) as any;
  if (edgeView?.update) {
    edgeView.update();
  }
}

let arcConnectorRegistered = false;

function ensureArcConnectorRegistered(): void {
  if (arcConnectorRegistered) return;
  arcConnectorRegistered = true;

  Graph.registerConnector(
    'invocation-arc',
    (sourcePoint: any, targetPoint: any, ...rest: any[]) => {
      const edgeView = rest.find(
        (arg: any) => arg && typeof arg === 'object' && arg.cell && typeof arg.cell.getData === 'function',
      );
      const edge = edgeView?.cell;
      const data = edge?.getData?.() || {};
      const cx = data._arcCenter?.x;
      const cy = data._arcCenter?.y;
      const radius = data._arcRadius;
      const startAngle = data._arcStartAngle;
      const endAngle = data._arcEndAngle;

      // NaN 安全检查
      if (
        typeof cx !== 'number' || typeof cy !== 'number' ||
        typeof radius !== 'number' || typeof startAngle !== 'number' || typeof endAngle !== 'number' ||
        Number.isNaN(cx) || Number.isNaN(cy) || Number.isNaN(radius) ||
        Number.isNaN(startAngle) || Number.isNaN(endAngle) || radius <= 0
      ) {
        return `M ${sourcePoint.x} ${sourcePoint.y} L ${targetPoint.x} ${targetPoint.y}`;
      }

      const arcStart = data._arcStartPoint
        ? { x: data._arcStartPoint.x, y: data._arcStartPoint.y }
        : { x: cx + Math.cos(startAngle) * radius, y: cy + Math.sin(startAngle) * radius };
      const arcEnd = data._arcEndPoint
        ? { x: data._arcEndPoint.x, y: data._arcEndPoint.y }
        : { x: cx + Math.cos(endAngle) * radius, y: cy + Math.sin(endAngle) * radius };

      // 用三次贝塞尔逼近圆弧，避免 SVG A 在大角度时的渲染限制
      // 公式: k = (4/3) * tan(θ/4) * r
      let rawDiff = endAngle - startAngle;
      while (rawDiff < -Math.PI) rawDiff += 2 * Math.PI;
      while (rawDiff > Math.PI) rawDiff -= 2 * Math.PI;

      const absDiff = Math.abs(rawDiff);
      if (absDiff < 1e-4) {
        return `M ${arcStart.x} ${arcStart.y} L ${arcEnd.x} ${arcEnd.y}`;
      }

      const r = Math.max(
        Math.hypot(arcStart.x - cx, arcStart.y - cy),
        Math.hypot(arcEnd.x - cx, arcEnd.y - cy),
        radius,
      );
      const k = (4 / 3) * Math.tan(absDiff / 4) * r;
      const sweep = rawDiff >= 0 ? 1 : -1;

      // 起点切线方向（垂直于半径，按 sweep 方向）
      const startTangentX = -Math.sin(startAngle) * sweep;
      const startTangentY = Math.cos(startAngle) * sweep;
      // 终点切线方向（垂直于半径，按 sweep 方向）
      const endTangentX = -Math.sin(endAngle) * sweep;
      const endTangentY = Math.cos(endAngle) * sweep;

      const cp1x = arcStart.x + startTangentX * k;
      const cp1y = arcStart.y + startTangentY * k;
      const cp2x = arcEnd.x - endTangentX * k;
      const cp2y = arcEnd.y - endTangentY * k;

      return `M ${arcStart.x} ${arcStart.y} C ${cp1x} ${cp1y} ${cp2x} ${cp2y} ${arcEnd.x} ${arcEnd.y}`;
    },
    true,
  );
}

function ensureAnchorRegistered(): void {
  if (anchorRegistered) return;
  anchorRegistered = true;

  Graph.registerAnchor(
    'invocation-source',
    function (this: any, nodeView: any, magnet: SVGElement, ref: any, args: any, type: any) {
      const node = nodeView?.cell as Node | null;
      if (!node?.isNode?.()) return Point.create(nodeView?.getBBox?.()?.center || { x: 0, y: 0 });
      const bbox = node.getBBox();
      const cx = bbox.x + bbox.width / 2;
      const cy = bbox.y + bbox.height / 2;
      // 从 edgeView.cell 读取 _fixedStartOffset
      const edgeView = this as any;
      const edge = edgeView?.cell;
      const data = edge?.getData?.() || {};
      if (data._fixedStartOffset && typeof data._fixedStartOffset.dx === 'number') {
        return Point.create({ x: cx + data._fixedStartOffset.dx, y: cy + data._fixedStartOffset.dy });
      }
      // 回退：按目标方向计算边界
      const targetCell = edge?.getTargetCell?.();
      if (targetCell?.isNode?.()) {
        const tBox = targetCell.getBBox();
        const tCx = tBox.x + tBox.width / 2;
        const tCy = tBox.y + tBox.height / 2;
        return Point.create(computeBoundaryPoint(node, { x: tCx, y: tCy }));
      }
      return Point.create({ x: cx, y: cy });
    },
    true,
  );
}

function ensureConnectorRegistered(): void {
  if (connectorRegistered) return;
  connectorRegistered = true;

  ensureAnchorRegistered();

  Graph.registerConnector(
    'invocation-zigzag',
    (sourcePoint: any, targetPoint: any, ...rest: any[]) => {
      let start = { x: sourcePoint.x, y: sourcePoint.y };
      let end = { x: targetPoint.x, y: targetPoint.y };

      const edgeView = rest.find(
        (arg: any) => arg && typeof arg === 'object' && arg.cell && typeof arg.cell.getSourceCell === 'function',
      );
      const edge = edgeView?.cell;
      if (edge) {
        const data = edge.getData?.() || {};
        const graph = edge.model?.graph;
        if (graph) {
          const sourceCell = edge.getSourceCell();
          const targetCell = edge.getTargetCell();
          const sourceTerminal = edge.getSource?.() as any;
          const sourceAnchorName = sourceTerminal?.anchor?.name;
          // 源端连接到节点时：使用 invocation-source 自定义锚点返回的实际起点；
          // 旧中心锚点则叠加 _fixedStartOffset
          if (sourceCell?.isNode?.()) {
            if (sourceAnchorName === 'invocation-source') {
              start = { x: sourcePoint.x, y: sourcePoint.y };
            } else if (data._fixedStartOffset && typeof data._fixedStartOffset.dx === 'number') {
              const bbox = sourceCell.getBBox();
              const cx = bbox.x + bbox.width / 2;
              const cy = bbox.y + bbox.height / 2;
              start = { x: cx + data._fixedStartOffset.dx, y: cy + data._fixedStartOffset.dy };
            } else {
              start = computeBoundaryPoint(sourceCell as Node, end);
            }
          }
          if (targetCell?.isNode?.()) {
            // 如果边有持久化的父节点吸附信息，使用它计算目标点（跟随父节点尺寸变化）
            if (data._parentAttachSide && typeof data._parentAttachRatio === 'number') {
              const tgtData = (targetCell as Node).getData?.() || {};
              if (tgtData.isParentCopy) {
                end = pointFromParentAttach(targetCell as Node, data._parentAttachSide, data._parentAttachRatio);
              } else {
                end = computeBoundaryPoint(targetCell as Node, start);
              }
            } else {
              end = computeBoundaryPoint(targetCell as Node, start);
            }
          }
        }
      }

      // NaN 安全检查
      if (Number.isNaN(start.x) || Number.isNaN(start.y) || Number.isNaN(end.x) || Number.isNaN(end.y)) {
        return `M 0 0 L 0 0`;
      }
      const dx = end.x - start.x;
      const dy = end.y - start.y;
      const length = Math.hypot(dx, dy);
      if (!length) return `M ${start.x} ${start.y} L ${end.x} ${end.y}`;
      const ux = dx / length;
      const uy = dy / length;
      const nx = -uy;
      const ny = ux;
      const midX = (start.x + end.x) / 2;
      const midY = (start.y + end.y) / 2;
      const p1 = {
        x: midX + ux * INVOCATION_ZIGZAG_HALF - nx * INVOCATION_ZIGZAG_OFFSET,
        y: midY + uy * INVOCATION_ZIGZAG_HALF - ny * INVOCATION_ZIGZAG_OFFSET,
      };
      const p2 = {
        x: midX - ux * INVOCATION_ZIGZAG_HALF + nx * INVOCATION_ZIGZAG_OFFSET,
        y: midY - uy * INVOCATION_ZIGZAG_HALF + ny * INVOCATION_ZIGZAG_OFFSET,
      };
      return `M ${start.x} ${start.y} L ${p1.x} ${p1.y} L ${p2.x} ${p2.y} L ${end.x} ${end.y}`;
    },
    true,
  );
}

const getInvocationParentNode = (sourceNode: Node, targetNode: Node) => {
  const srcParent = sourceNode.getParent?.();
  const tgtParent = targetNode.getParent?.();
  const srcData = sourceNode.getData?.() || {};
  const tgtData = targetNode.getData?.() || {};

  if (srcParent && tgtParent && srcParent.id === tgtParent.id && srcParent.isNode?.()) {
    return srcParent as Node;
  }
  if (srcParent?.isNode?.() && tgtData.isParentCopy && srcParent.id === targetNode.id) {
    return targetNode;
  }
  if (tgtParent?.isNode?.() && srcData.isParentCopy && tgtParent.id === sourceNode.id) {
    return sourceNode;
  }
  return null;
};

export function applyInvocationZigzagToEdge(edge: Edge, graph: Graph, force = false): void {
  const data = edge.getData?.() || {};
  if (!force && data.edgeSubtype !== 'invocation') return;
  if (data._manualVertices) return;
  if (isInvocationAngleGuide(data)) return;

  ensureConnectorRegistered();

  const sourceId = edge.getSourceCellId?.();
  const targetId = edge.getTargetCellId?.();
  const sourceNode = sourceId ? (graph.getCellById(sourceId) as Node | null) : null;
  const targetNode = targetId ? (graph.getCellById(targetId) as Node | null) : null;

  // 记录原始源节点 ID，用于 source 被拖拽为坐标点后的分组
  const updatedData: Record<string, any> = { ...data };
  if (sourceId && data._invocationSourceNodeId !== sourceId) {
    updatedData._invocationSourceNodeId = sourceId;
  }

  // 计算并存储固定的起点（相对于源节点中心的偏移），绑定到当前目标节点 ID
  // 只有目标节点变化、没有记录偏移、且非手动设置时才重新计算，避免移动子节点改变出线点
  if (sourceNode?.isNode?.() && targetNode?.isNode?.()) {
    const hasFixedOffset = data._fixedStartOffset && typeof data._fixedStartOffset.dx === 'number';
    const fixedForTarget = data._fixedStartForTargetId;
    if (!data._fixedStartManual && (!hasFixedOffset || fixedForTarget !== targetId)) {
      const sourceCenter = getNodeCenter(sourceNode);
      const targetCenter = getNodeCenter(targetNode);
      const fixedStart = computeBoundaryPoint(sourceNode, targetCenter);
      updatedData._fixedStartOffset = {
        dx: fixedStart.x - sourceCenter.x,
        dy: fixedStart.y - sourceCenter.y,
      };
      updatedData._fixedStartForTargetId = targetId;
    }
  }
  edge.setData(updatedData);

  // 只在首次配置时设置 source/target，避免拖拽时重复 setSource/setTarget 导致卡顿
  // 如果 source/target 是坐标点（手动拖拽），不覆盖
  const currentSource = edge.getSource?.() as any;
  const currentTarget = edge.getTarget?.() as any;
  const sourceIsCoord = typeof currentSource?.x === 'number' && !currentSource?.cell;
  const targetIsCoord = typeof currentTarget?.x === 'number' && !currentTarget?.cell;
  const sourceNeedsUpdate = !sourceIsCoord && (!currentSource?.anchor || currentSource.anchor?.name !== 'invocation-source' || currentSource.connectionPoint?.name !== 'anchor');
  const targetNeedsUpdate = !targetIsCoord && (!currentTarget?.anchor || currentTarget.anchor?.name !== 'center' || currentTarget.connectionPoint?.name !== 'anchor');

  if (sourceNode?.isNode?.() && sourceNeedsUpdate) {
    edge.setSource({
      cell: sourceNode.id,
      anchor: { name: 'invocation-source' },
      connectionPoint: { name: 'anchor' },
    });
  }
  if (targetNode?.isNode?.() && targetNeedsUpdate) {
    edge.setTarget({
      cell: targetNode.id,
      anchor: { name: 'center' },
      connectionPoint: { name: 'anchor' },
    });
  }

  edge.setVertices([]);
  edge.setRouter({ name: 'normal' });
  edge.setConnector('invocation-zigzag');

  const parentNode = sourceNode?.isNode?.() && targetNode?.isNode?.()
    ? getInvocationParentNode(sourceNode, targetNode)
    : null;

  if (edge.setZIndex) {
    // 确保 invocation zigzag 边永远在最上层，防止对角线被节点填充遮挡
    edge.setZIndex(9999);
  }
}

export function syncInvocationAngleGuides(graph: Graph): void {
  const existingGuides = graph.getEdges().filter(edge => {
    const data = edge.getData?.() || {};
    return isInvocationAngleGuide(data);
  });
  existingGuides.forEach(edge => edge.remove());

  // 收集所有结构性边（invocation + 子-父边等），按源节点分组
  const structuralEdges = graph.getEdges().filter(edge => {
    const data = edge.getData?.() || {};
    if (isInvocationAngleGuide(data)) return false;
    return data.edgeCategory === 'structural' || data.edgeSubtype === 'invocation';
  });

  const groups = new Map<string, Edge[]>();
  structuralEdges.forEach(edge => {
    let sourceId = edge.getSourceCellId?.();
    // source 被拖拽为坐标点时，使用记录的原始源节点 ID 分组
    if (!sourceId) {
      const d = edge.getData?.() || {};
      sourceId = d._invocationSourceNodeId;
    }
    if (!sourceId) return;
    const group = groups.get(sourceId) || [];
    group.push(edge);
    groups.set(sourceId, group);
  });

  groups.forEach(group => {
    if (group.length < 2) return;
    let sourceId = group[0].getSourceCellId?.();
    if (!sourceId) {
      const d = group[0].getData?.() || {};
      sourceId = d._invocationSourceNodeId;
    }
    if (!sourceId) return;
    const sourceNode = graph.getCellById(sourceId) as Node | null;
    if (!sourceNode?.isNode?.()) return;

    const sourceCenter = getNodeCenter(sourceNode);

    // 计算每条边的实际起点（边界交点或手动拖拽坐标）及其目标中心
    const starts = group.map(edge => {
      const src = edge.getSource?.() as any;
      const targetId = edge.getTargetCellId?.();
      const targetNode = targetId ? (graph.getCellById(targetId) as Node | null) : null;
      if (!targetNode?.isNode?.()) return null;
      const targetCenter = getNodeCenter(targetNode);
      const edgeData = edge.getData?.() || {};
      // 手动拖拽到坐标点
      if (typeof src?.x === 'number' && !src?.cell) {
        const start = { x: src.x, y: src.y };
        const firstPoint = edgeData.edgeSubtype === 'invocation'
          ? computeInvocationFirstSegmentPoint(start, computeBoundaryPoint(targetNode, start))
          : computeBoundaryPoint(targetNode, start);
        return {
          start,
          targetCenter,
          isCoord: true,
          outAngle: Math.atan2(firstPoint.y - start.y, firstPoint.x - start.x),
          firstSegmentLength: Math.hypot(firstPoint.x - start.x, firstPoint.y - start.y),
          firstPoint: { x: firstPoint.x, y: firstPoint.y },
        };
      }
      // 连接到节点：使用固定的起点偏移
      let start: { x: number; y: number };
      if (edgeData._fixedStartOffset && typeof edgeData._fixedStartOffset.dx === 'number') {
        start = { x: sourceCenter.x + edgeData._fixedStartOffset.dx, y: sourceCenter.y + edgeData._fixedStartOffset.dy };
      } else {
        start = computeBoundaryPoint(sourceNode, targetCenter);
      }

      let endPoint: { x: number; y: number };
      if (edgeData._parentAttachSide && typeof edgeData._parentAttachRatio === 'number') {
        const tgtData = targetNode.getData?.() || {};
        if (tgtData.isParentCopy) {
          endPoint = pointFromParentAttach(targetNode, edgeData._parentAttachSide, edgeData._parentAttachRatio);
        } else {
          endPoint = computeBoundaryPoint(targetNode, start);
        }
      } else {
        endPoint = computeBoundaryPoint(targetNode, start);
      }

      let outAngle: number;
      let firstSegmentLength: number;
      let firstPoint: { x: number; y: number };
      if (edgeData.edgeSubtype === 'invocation') {
        firstPoint = computeInvocationFirstSegmentPoint(start, endPoint);
        outAngle = Math.atan2(firstPoint.y - start.y, firstPoint.x - start.x);
        firstSegmentLength = Math.hypot(firstPoint.x - start.x, firstPoint.y - start.y);
      } else {
        const vertices = edge.getVertices?.() || [];
        const firstVertex = vertices.find((v: { x: number; y: number }) => Math.hypot(v.x - start.x, v.y - start.y) > 1e-3);
        firstPoint = firstVertex || endPoint;
        outAngle = Math.atan2(firstPoint.y - start.y, firstPoint.x - start.x);
        firstSegmentLength = Math.hypot(firstPoint.x - start.x, firstPoint.y - start.y);
      }

      return { start, targetCenter, isCoord: false, outAngle, firstSegmentLength, firstPoint: { x: firstPoint.x, y: firstPoint.y } };
    }).filter(Boolean) as Array<{ start: { x: number; y: number }; targetCenter: { x: number; y: number }; isCoord: boolean; outAngle: number; firstSegmentLength: number; firstPoint: { x: number; y: number } }>;

    if (starts.length < 2) return;

    // 同一合并起点可能连接多条边：按阈值聚类，找出所有“起点重合”的边簇
    const MERGE_THRESHOLD = 1;
    const clusters: Array<{ center: { x: number; y: number }; items: typeof starts }> = [];
    starts.forEach(s => {
      const found = clusters.find(c => Math.hypot(c.center.x - s.start.x, c.center.y - s.start.y) <= MERGE_THRESHOLD);
      if (found) {
        found.items.push(s);
        const n = found.items.length;
        found.center = {
          x: (found.center.x * (n - 1) + s.start.x) / n,
          y: (found.center.y * (n - 1) + s.start.y) / n,
        };
      } else {
        clusters.push({ center: { x: s.start.x, y: s.start.y }, items: [s] });
      }
    });

    const sourceZ = sourceNode.getZIndex?.();
    ensureArcConnectorRegistered();

    clusters.forEach(cluster => {
      if (cluster.items.length < 2) return;

      // 取最小包络角：去掉最大空隙后，剩余区间即覆盖所有边的最小角度
      const orderedAngles = findMinimalCoveringOrder(cluster.items.map(item => item.outAngle));
      if (orderedAngles.length < 2) return;

      const arcCenter = cluster.center;

      // 逐段连接相邻方向：n 条边生成 n-1 段虚线，完整连接所有边
      const radius = 20;
      // 建立角度到首段点的映射
      const angleToPoint = new Map<number, { x: number; y: number }>();
      cluster.items.forEach(item => {
        // 用首段方向上的点作为弧端点，确保贴合边线
        const itemRadius = Math.min(radius, Math.max(6, item.firstSegmentLength - 1));
        const pt = {
          x: arcCenter.x + Math.cos(item.outAngle) * itemRadius,
          y: arcCenter.y + Math.sin(item.outAngle) * itemRadius,
        };
        angleToPoint.set(item.outAngle, pt);
      });
      for (let i = 0; i < orderedAngles.length - 1; i++) {
        const arcStartAngle = orderedAngles[i];
        const arcEndAngle = orderedAngles[i + 1];
        const arcStartPt = angleToPoint.get(arcStartAngle) || {
          x: arcCenter.x + Math.cos(arcStartAngle) * radius,
          y: arcCenter.y + Math.sin(arcStartAngle) * radius,
        };
        const arcEndPt = angleToPoint.get(arcEndAngle) || {
          x: arcCenter.x + Math.cos(arcEndAngle) * radius,
          y: arcCenter.y + Math.sin(arcEndAngle) * radius,
        };
        // source/target 设为完整圆的包围盒，防止 X6 裁剪大角度弧线
        const guide = graph.addEdge({
          shape: 'edge',
          source: { x: arcCenter.x - radius, y: arcCenter.y - radius },
          target: { x: arcCenter.x + radius, y: arcCenter.y + radius },
          vertices: [],
          router: { name: 'normal' },
          connector: 'invocation-arc',
          attrs: {
            line: {
              stroke: '#6b7a90',
              strokeWidth: 2.2,
              strokeDasharray: '3 2',
              sourceMarker: null,
              targetMarker: null,
              pointerEvents: 'none',
            },
          },
          data: {
            [INVOCATION_GUIDE_TAG]: true,
            edgeCategory: 'auxiliary',
            _arcCenter: { x: arcCenter.x, y: arcCenter.y },
            _arcRadius: radius,
            _arcStartAngle: arcStartAngle,
            _arcEndAngle: arcEndAngle,
            _arcSweepFlag: 1,
            _arcStartPoint: { x: arcStartPt.x, y: arcStartPt.y },
            _arcEndPoint: { x: arcEndPt.x, y: arcEndPt.y },
          },
        });

        if (guide.setZIndex) {
          // 角度引导线必须始终在节点之上，避免进入节点内容时被遮挡
          guide.setZIndex(9999);
        }
      }
    });
  });
}

export function applyInvocationZigzag(graph: Graph): void {
  graph.getEdges().forEach(edge => applyInvocationZigzagToEdge(edge, graph));
  syncInvocationAngleGuides(graph);
}
